import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

// Rxjs
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { CookieService } from 'ngx-cookie-service';

// Material
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { NgxPermissionsService } from 'ngx-permissions';

// Services
import { AuthService } from '@auth/auth.service';
import { WebcomponentsService } from '@core/service/webcomponents.service';
import { MarketingEventEnumeration, MarketingEventService } from '@shared/services';
import { RedirectionService } from '@shared/services/redirection.service';
import { HeaderService } from './header.service';

// Components
import { LockCardDialogComponent } from './components/lock-card-dialog';
import { OtpDialogComponent } from './components/otp-dialog';

// Configs
import { HEADER_ROUTES_CONSTANTS, VIRTUAL_CARD_STATUS_ENUMERATION } from './configs';

// Models
import { FilterHome } from '@model/pay/filter-home';
import { Rol } from '@model/rol';
import { CardResponseType } from './models';

// Environments
import { environment } from '@environments/environment';

// Enums
import { COOKIE_ENUMERATION } from '@home/login/configs';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
})
export class HeaderComponent implements OnInit, AfterViewInit {
  @ViewChild('notificationsRef', { static: false }) public notificationWebComponent: ElementRef;
  @ViewChild('sidenav') public sidenav: MatSidenav;
  @ViewChild('sidenav') public sidenavElementRef: ElementRef;

  private readonly webComponentService: WebcomponentsService;
  private readonly cookieService: CookieService;
  private readonly marketingEventService: MarketingEventService;
  private readonly router: Router;
  private readonly service: HeaderService;
  private readonly roleStoreService: NgxPermissionsService;
  private readonly redirectService: RedirectionService;
  private readonly _changeDetectionRef: ChangeDetectorRef;
  private readonly dialog: MatDialog;

  public readonly authService: AuthService;

  private readonly unSubscribe$: Subject<void>;

  public userName: string;
  public rol: Rol;
  public isAuthenticated: boolean;
  public permissions: string[];

  public filterPay: string;
  public filterDetail: string;
  public showMenu: boolean;
  public hasActiveLoan: boolean;
  public isProduction: boolean;
  public showTooltip: boolean;
  public isOptionVisible: boolean;
  public HEADER_ROUTES_CONSTANTS: typeof HEADER_ROUTES_CONSTANTS;

  public cardInfo: CardResponseType | null;

  constructor(private readonly injector: Injector) {
    this.webComponentService = this.injector.get(WebcomponentsService);
    this.cookieService = this.injector.get(CookieService);
    this.marketingEventService = this.injector.get(MarketingEventService);
    this.router = this.injector.get(Router);
    this.service = this.injector.get(HeaderService);
    this.authService = this.injector.get(AuthService);
    this.roleStoreService = this.injector.get(NgxPermissionsService);
    this.redirectService = this.injector.get(RedirectionService);
    this._changeDetectionRef = this.injector.get(ChangeDetectorRef);
    this.dialog = this.injector.get(MatDialog);

    this.permissions = [];
    this.showMenu = false;
    this.unSubscribe$ = new Subject();
    this.isProduction = environment.production;
    this.hasActiveLoan = true;
    this.isOptionVisible = false;
    this.HEADER_ROUTES_CONSTANTS = HEADER_ROUTES_CONSTANTS;
    this.showTooltip = false;
    this.cardInfo = null;

    this.userName = '';

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((navegacion: NavigationEnd) => {
        this.redirectService.registerUrl(navegacion.url);
      });
  }

  public ngAfterViewInit(): void {
    this.initNotificationsWebComponent();
  }

  public ngOnInit(): void {
    this.rol = new Rol();
    this.filterPay = FilterHome.PAY;
    this.filterDetail = FilterHome.DETAIL;
    this.getUser();
    this.service.getMenuControl().subscribe((status) => {
      if (status?.updateUserInfo) this.getUser();
      this._changeDetectionRef.detectChanges();
    });
    this.activeCard();
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  public toggleOption(): void {
    this.isOptionVisible = !this.isOptionVisible;
  }

  public initNotificationsWebComponent(): void {
    const token = this.authService.token;
    const payload = this.authService.getTokenData(token);
    const notifications = this.notificationWebComponent.nativeElement;

    this.webComponentService.isLoaded$.pipe(takeUntil(this.unSubscribe$)).subscribe({
      next: (loaded) => {
        if (loaded) {
          notifications.ngElementStrategy.componentRef.instance.build({
            clientId: payload.clienteId,
          });
        }
      },
    });
  }

  public logout(): void {
    this.authService.logout();
  }

  public getUser(): void {
    this.service.getUserInformation().subscribe((response: { response: string }) => {
      this.userName = response.response;
      if (this.userName == 'null null') {
        this.userName = '';
      }
    });

    this.rol.roles = this.authService.getRoles();

    for (const rol of this.rol.roles) {
      this.permissions.push(rol.name);
    }

    // es una libreria que cuando se cargan las diferentes pantallas se cargan los roles en los submenus

    // en este momento existen unos roles doomies
    this.roleStoreService.loadPermissions(this.permissions);
  }

  public detectClickOutsideSidenav(event: MouseEvent): void {
    if (!this.showMenu) return;

    const clickedInsideSidenav = this.sidenavElementRef.nativeElement?.contains(event.target);
    if (!clickedInsideSidenav) {
      this.sidenav.close();
      this.showMenu = false;
    }
  }

  public toggleOpenSideNav(): void {
    this.showMenu = !this.showMenu;
    if (this.showMenu) {
      this.sidenav.open();
    } else {
      this.sidenav.close();
    }
  }

  public closeTooltip(): void {
    this.showTooltip = false;
  }

  public redirectRF(): void {
    this.marketingEventService.sendMarketingEvents(MarketingEventEnumeration.LOGIN_RF);
    this.cookieService.set(COOKIE_ENUMERATION.CRFRAP, this.authService.token, 1, '/', environment.domain);
    window.location.href = environment.myCreditsRapiflex;
  }

  public activeCard(): void {
    this.service
      .activeCard()
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((response) => {
        this.cardInfo = response;
      });
  }

  public cardIsActive(): boolean {
    return this.cardInfo?.status === VIRTUAL_CARD_STATUS_ENUMERATION.ACTIVE;
  }

  public toggleLockCard(): void {
    if (this.cardIsActive()) {
      this.lockCard();
    }
  }

  private lockCard(): void {
    const dialogRef = this.dialog.open(LockCardDialogComponent, {
      width: '320px',
      disableClose: false,
      data: {
        cardId: this.cardInfo?.id,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        this.activeCard();
      }
    });
  }

  public unlockCard(): void {
    if (this.cardIsActive()) return;
    const token = this.authService.token;
    const payload = this.authService.getTokenData(token);

    const dialogRef = this.dialog.open(OtpDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        clientId: payload.clienteId,
        otpType: 'CARD',
        cardId: this.cardInfo?.id,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.activeCard();
      }
    });
  }

  public redirectToFAQ(): void {
    window.open(environment.faqUrl, '_blank', 'noopener,noreferrer');
  }
}
